import type { DrillProgress } from "../handwriting/xiezi-stages.js";
import { limitedAttribute } from "../content/lianxi/exercise-fields.js";
import { withinTaste } from "../handwriting/xiezi-taste.js";

/**
 * The gate a reader without an account meets part way through a drill or an
 * Exercise 练习.
 *
 * A visitor does the first part of each and is then asked to sign up to
 * finish it (Hugh, 2026-10-16). `src/handwriting/xiezi-taste.ts` says how much
 * of a drill that is, and a grammar exercise's block names the count of its
 * items in `data-cb-lianxi-taste`, which `src/content/lianxi/yufa-taste.ts`
 * works out at build time. `partials/lianxi/gate.html` and
 * `src/cidian-page/cidian-xiezi-markup.ts` draw the gate hidden, and this
 * shows it.
 *
 * **The gate is soft.** The answers are in the page already, and a reload
 * starts the taste again. What it holds back is the stages written from
 * memory, the place kept and the hand-over to 复习, which are what an account
 * is for. The markup is unchanged, so a search engine reads the whole
 * Exercise.
 */

/** Whether the page was drawn for somebody signed in, as `head/session-state.html` says. */
export function signedIn(block: HTMLElement): boolean {
  return block.ownerDocument.documentElement.dataset["cbSession"] === "in";
}

/** The gate inside a block, drawn hidden. */
function gateOf(block: HTMLElement): HTMLElement | null {
  return block.querySelector<HTMLElement>("[data-cb-lianxi-gate]");
}

/**
 * Whether a block stops at the taste.
 *
 * A reader the session hint names as signed in plays to the end, whether or
 * not the handler kept their place, unless the handler said they have no new
 * Exercise left to start today. A stale hint is rare, and a gate asking
 * somebody with an account to sign up would be wrong more often than the
 * hint is. A block drawn with no gate also plays to the end.
 * @param block - The drill's or the Exercise's block.
 * @returns Whether the reader has no account on this page and the block has
 * a gate to show them.
 */
export function tasting(block: HTMLElement): boolean {
  return (!signedIn(block) || limited(block)) && gateOf(block) !== null;
}

/**
 * Whether the handler said a free reader has no new Exercise left to start
 * today, which `./lianxi-place-write.ts` marks.
 */
function limited(block: HTMLElement): boolean {
  return block.dataset[limitedAttribute] !== undefined;
}

/**
 * Show the gate and put the drill or the items out of the way.
 *
 * The progress over the Exercise goes too, whatever kind draws it
 * (`data-cb-lianxi-progress`). It counts the questions or the stages of this
 * Exercise, and left above a gate that names the day's five starts it read
 * as a count of those (CB-54).
 * @param block - The block holding the gate.
 */
export function showGate(block: HTMLElement): void {
  const gate = gateOf(block);

  if (gate === null) {
    return;
  }

  block.dataset["cbLianxiTasted"] = "";
  gate.hidden = false;

  for (const progress of block.querySelectorAll<HTMLElement>(
    "[data-cb-lianxi-progress]",
  )) {
    progress.hidden = true;
  }

  for (const part of gate.querySelectorAll<HTMLElement>(
    "[data-cb-lianxi-gate-for]",
  )) {
    part.hidden =
      part.dataset["cbLianxiGateFor"] !==
      (limited(block) ? "limited" : "visitor");
  }
}

/**
 * What stops a visitor at the taste, or nothing for a reader who plays to the
 * end.
 * @param block - The block holding the gate.
 * @returns What shows the gate.
 */
export function tasteGate(block: HTMLElement): (() => void) | undefined {
  return tasting(block)
    ? (): void => {
        showGate(block);
      }
    : undefined;
}

/**
 * What a drill on a 词典 entry is told after each pass, which shows the gate
 * once the pass takes a visitor past the taste. A reader who plays to the end
 * is told nothing by it.
 * @param block - The drill's block.
 * @returns What hears each pass.
 */
export function passGate(
  block: HTMLElement,
): (progress: DrillProgress) => void {
  const gate = tasteGate(block);

  return (progress): void => {
    if (!withinTaste(progress)) {
      gate?.();
    }
  };
}

/**
 * What a grammar exercise asks before it shows an item: whether the item is
 * past the taste the block names in `data-cb-lianxi-taste`, in which case the
 * gate is shown in its place. A block naming no count it can read has no gate.
 * @param block - The exercise's block.
 * @returns What answers for an item by its place, or nothing for a reader who
 * plays to the end.
 */
export function itemGate(
  block: HTMLElement,
): ((at: number) => boolean) | undefined {
  const gate = tasteGate(block);
  const taste = Number.parseInt(block.dataset["cbLianxiTaste"] ?? "", 10);

  return gate && taste >= 0
    ? (at): boolean => {
        const past = at >= taste;

        if (past) {
          gate();
        }

        return past;
      }
    : undefined;
}
